import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import RegistrationModal from '@/components/feature/RegistrationModal';

const navItems = [
  { label: '首页', path: '/' },
  { label: '关于我们', path: '/about' },
  { label: '核心业务', path: '/business' },
  { label: '蚂蚁量化', path: '/quant' },
  { label: '产品下载', path: '/downloads' },
  { label: '合作平台', path: '/cooperation' },
  { label: '新闻动态', path: '/news' },
  { label: '联系我们', path: '/contact' },
];

function LiveSystemIndicator() {
  const [status, setStatus] = useState<'healthy' | 'degraded'>('healthy');
  const [latency, setLatency] = useState(4);

  useEffect(() => {
    const check = setInterval(() => {
      const r = Math.random();
      if (r < 0.92) {
        setStatus('healthy');
        setLatency(Math.floor(Math.random() * 6) + 2);
      } else {
        setStatus('degraded');
        setLatency(Math.floor(Math.random() * 30) + 12);
        setTimeout(() => {
          setStatus('healthy');
          setLatency(Math.floor(Math.random() * 6) + 2);
        }, 4000 + Math.random() * 3000);
      }
    }, 8000);
    return () => clearInterval(check);
  }, []);

  return (
    <div className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-background-300/30 bg-background-200/30">
      <div className="relative w-1.5 h-1.5 flex items-center justify-center">
        <span
          className={`absolute inset-0 rounded-full ${
            status === 'healthy' ? 'bg-emerald-400 animate-pulse-glow' : 'bg-accent-400 animate-blink'
          }`}
        ></span>
      </div>
      <span
        className={`text-[9px] mono-data font-semibold tracking-wider ${
          status === 'healthy' ? 'text-emerald-400' : 'text-accent-400'
        }`}
      >
        {status === 'healthy' ? 'LIVE' : 'DEGR'}
      </span>
      <span className="text-[8px] mono-data text-foreground-600">{latency}ms</span>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [regModalOpen, setRegModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-xl border-b border-background-300/30'
          : isHome
            ? 'bg-background-50/50 backdrop-blur-sm border-b border-background-300/10'
            : 'bg-background-50/95 backdrop-blur-xl border-b border-background-300/30'
      }`}
    >
      <div className="w-full px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0 group">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center relative">
              <i className={`ri-funds-line text-xl md:text-2xl transition-all duration-300 ${scrolled || !isHome ? 'text-primary-400' : 'text-white'} group-hover:text-accent-400`}></i>
            </div>
            <span
              className={`text-sm md:text-base font-semibold font-heading whitespace-nowrap transition-colors duration-300 ${
                scrolled || !isHome ? 'text-foreground-100' : 'text-white'
              }`}
            >
              香港蚂蚁链控股集团
            </span>
            {/* ── Global LIVE System Indicator ── */}
            <LiveSystemIndicator />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-3 py-2 text-sm font-medium whitespace-nowrap rounded-lg transition-all duration-300 ${
                    scrolled || !isHome
                      ? isActive
                        ? 'text-accent-400'
                        : 'text-foreground-400 hover:text-foreground-100 hover:bg-background-200/50'
                      : isActive
                        ? 'text-accent-400'
                        : 'text-white/75 hover:text-white hover:bg-white/8'
                  }`}
                >
                  {item.label}
                  {isActive && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-accent-500 rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right: CTA */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => setRegModalOpen(true)}
              className={`relative flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                scrolled || !isHome
                  ? 'bg-accent-500/12 text-accent-500 border border-accent-500/25 hover:bg-accent-500 hover:text-background-950'
                  : 'bg-white/10 text-white border border-white/20 hover:bg-accent-500 hover:text-background-950 hover:border-accent-500'
              }`}
            >
              {/* Pulse rings */}
              <span className="absolute inset-0 rounded-full border border-accent-400/40 animate-cta-pulse-ring pointer-events-none"></span>
              <span className="absolute inset-0 rounded-full border border-accent-400/30 animate-cta-pulse-ring pointer-events-none" style={{ animationDelay: '0.8s' }}></span>
              <span className="absolute inset-0 rounded-full border border-accent-400/20 animate-cta-pulse-ring pointer-events-none" style={{ animationDelay: '1.6s' }}></span>
              <i className="ri-user-add-line text-sm relative z-10"></i>
              <span className="relative z-10">报名使用产品</span>
            </button>
            <Link
              to="/contact"
              className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                scrolled || !isHome
                  ? 'bg-accent-500 text-background-950 hover:bg-accent-400 shadow-[0_0_20px_rgba(200,160,80,0.15)]'
                  : 'bg-accent-500/90 text-background-950 hover:bg-accent-400 shadow-[0_0_20px_rgba(200,160,80,0.2)]'
              }`}
            >
              业务接洽
              <i className="ri-arrow-right-up-line text-sm"></i>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i
              className={`text-xl transition-colors duration-300 ${
                scrolled || !isHome ? 'text-foreground-200' : 'text-white'
              } ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-xl border-t border-background-300/30">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-500/15 text-accent-400 border-l-2 border-accent-500'
                      : 'text-foreground-400 hover:bg-background-200/50 hover:text-foreground-100'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <button
              onClick={() => { setRegModalOpen(true); setMobileOpen(false); }}
              className="mt-2 flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-sm font-semibold bg-accent-500/12 text-accent-500 border border-accent-500/25 hover:bg-accent-500 hover:text-background-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-user-add-line text-sm"></i>
              报名使用产品
            </button>
            <Link
              to="/contact"
              className="mt-2 flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-sm font-semibold bg-accent-500 text-background-950 hover:bg-accent-400 transition-colors"
            >
              业务接洽
              <i className="ri-arrow-right-up-line text-sm"></i>
            </Link>
          </div>
        </div>
      )}
      <RegistrationModal open={regModalOpen} onClose={() => setRegModalOpen(false)} />
    </nav>
  );
}